import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginAsEmployee, logout } from "../../redux/auth/authSlice";
import { postApi, putApi } from "../../api/api_instance";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { API_URLS } from "../../api/apiUrls";
import { ReactComponent as Mail } from "../../assets/svgs/mail.svg";
import { ReactComponent as Logo } from "../../assets/svgs/logo.svg";
import { InputField } from "../../components/Elements/InputField/InputField";
import { SideImage } from "./components/SideImage";
import { Button } from "antd";
import { AcceptDialog } from "../../components/Elements/DialogBox/AcceptDialog";
import { Eye, EyeOff } from "lucide-react";
import PopupCard from "./PopupCard";
import { CheckOutlined } from "@ant-design/icons";
import { Modal } from "antd";
export const LoginEmployee = ({ onSignup, onResetPassword }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { employeeLogin } = useSelector((state) => state.auth);

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showCard, setShowCard] = useState(false); // For animation mount
  const [popupData, setPopupData] = useState({
    type: "success",
    title: "Success",
    message: "",
  });

  useEffect(() => {
    setTimeout(() => setShowCard(true), 100); // Trigger mount animation
  }, []);

  const triggerPopup = (type, title, message, duration = 2000) => {
    setPopupData({ type, title, message });
    setShowPopup(true);
    setTimeout(
      () => setShowPopup(false),

      duration,
    );
  };
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData({ ...loginData, [name]: value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!loginData.email || !loginData.password) {
      //toast.error("Please enter both email and password");
      triggerPopup(
        "error",
        "Verification Failed",
        "Please enter both email and password",
      );
      return;
    }

    setIsLoading(true);

    try {
      const response = await postApi({
        apiUrl: API_URLS.LOGIN_EMPLOYEE,
        data: {
          email: loginData.email,
          password: loginData.password,
          client_id: orgId,
        },
      });

      if (response.success) {
        const { data } = response;
        const user = {
          _id: data._id,
          name: data.name,
          employee_id: data.employee_id,
          email: data.email,
          contact_no: data.contact_no,
          subAdmin: data.subAdmin,
          managementView: data.managementView,
          locationId: data.locationId,
          departmentId: data.departmentId,
          isCompleted: data.isCompleted,
          client_id: data.client_id,
          isFirstLogin: data.isFirstLogin,
          // tempPassword: loginData.password,
          role: data.role || [],
        };
        const token = data.token || response.token;

        dispatch(loginAsEmployee({ user, token }));
        // in login success
        localStorage.setItem("orgId", orgId);

        if (data.isFirstLogin) {
          // If first login, ResetPasswordModal will render. Redirection will happen after password reset.
          return;
        }

        const hasDashboardPerm =
          (data.subAdmin?.isSubAdmin &&
            data.subAdmin.permissions?.some(
              (p) => p.module === "dashboard" || p.module === "dashbaord",
            )) ||
          (data.managementView?.isManagementView &&
            data.managementView.permissions?.some(
              (p) => p.module === "dashboard" || p.module === "dashbaord",
            )) ||
          data.managementView?.isManagementView;

        if (hasDashboardPerm) {
          navigate("/organization/dashboard", {
            state: { showWelcomeModal: true },
          });
        } else {
          navigate("/employee/home", { state: { showWelcomeModal: true } });
        }
      } else {
        // Handle case when response.success is false (invalid credentials)
        // toast.error(response.message || "Invalid credentials");
        triggerPopup(
          "error",
          "Verification Failed",
          response.message || "Invalid credentials",
        );
      }
    } catch (error) {
      //toast.error("Login failed. Please check your credentials and try again.");
      // Optional: trigger shake animation here
      triggerPopup(
        "error",
        "Verification Failed",
        "Login failed. Please check your credentials and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    const savedOrgId = localStorage.getItem("orgId");
    dispatch(logout());
    //toast.success("Logged out successfully");
    triggerPopup("success", "Success", "Logged out successfully");
    navigate(`/employee/login/${savedOrgId}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-12 overflow-hidden">
      <div
        className={`bg-white py-8 px-6 rounded-xl shadow-xl w-full max-w-md sm:px-10 border border-gray-200 transform transition-all duration-500 ${
          showCard ? "animate-fadeSlideIn" : "opacity-0 translate-y-8"
        }`}
      >
        <h2 className="text-center text-2xl font-bold text-[#06513D] mb-2">
          CARBURN
        </h2>
        <p className="text-center text-sm text-gray-600 mb-8">
          Carbon Footprint & ESG Reporting
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
          Welcome Back
        </h3>

        <form className="space-y-6" onSubmit={handleLogin}>
          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="Enter your email"
              value={loginData.email}
              onChange={handleLoginChange}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-[#06513D] sm:text-sm"
              disabled={isLoading}
            />
          </div>

          {/* Password Field */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>

              <button
                type="button"
                className="text-sm text-[#06513D]"
                onClick={() =>
                  navigate(
                    `/employee/resetpassword${orgId ? `?orgId=${orgId}` : ""}`,
                  )
                }
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                placeholder="Enter your password"
                value={loginData.password}
                onChange={handleLoginChange}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-[#06513D] sm:text-sm"
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Remember Me */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-700"
              >
                Remember me
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-md text-sm font-medium text-white bg-[#06513D] hover:bg-[#054a36] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing In...
                </div>
              ) : (
                "Sign In as Employee"
              )}
            </button>
          </div>
        </form>
        {/* )} */}

        {/* Sign Up Link */}
        {onSignup && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <button
                type="button"
                className="font-medium text-[#06513D] hover:underline"
                onClick={onSignup}
                disabled={isLoading}
              >
                Sign up
              </button>
            </p>
          </div>
        )}
      </div>

      {/* Tailwind Custom Animations */}
      <style>{`
        @keyframes fadeSlideIn {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeSlideIn {
          animation: fadeSlideIn 0.5s ease-out forwards;
        }
      `}</style>
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <PopupCard
            type={popupData.type}
            title={popupData.title}
            message={popupData.message}
          />
        </div>
      )}
    </div>
  );
};
